import type ILoginService from '../login.service';
import type { CodeStatusType, ILoginCodeSettings } from '../login.types';
import type { IToasterService } from 'ngtoaster';
import type { IController, IIntervalService, IPromise, IQService, ISCEService, IScope, IWindowService, translate } from 'angular';
import type { ILoginStatus, LoginSteps } from './loginStatus.controller';
import type { ICaptchaService } from '../../../_common/captcha/captcha.service';
import { IPhoneService } from '../../services/phone/phone.service';
import { Captcha, isCaptchaCancelled } from '../../../_common/captcha/captcha.types';
import { IOnSendCodeResponse } from '@/scripts/_common/code-input/codeInput.types';
import LoginStatusController from './loginStatus.controller';
export interface ILoginCodeController extends IController, ILoginStatus<CodeStatusType> {
  phone?: string;
  code?: string;
  enableRetry: boolean;
  countdownSeconds: number;
  timerHtml: string;
  isNeedRegistered?: boolean;
  phoneWatcher?: () => void;
  sendPhone(): void;
  sendCode(): IPromise<IOnSendCodeResponse>;
  setCountdown(seconds: number): void;
  confirmCode(code: string): IPromise<unknown> | void;
}
export default class LoginCodeController extends LoginStatusController<CodeStatusType> implements ILoginCodeController {
  phone?: string;
  code?: string;
  status: CodeStatusType = 'phone';
  settings?: ILoginCodeSettings;
  enableRetry = true;
  countdownSeconds = 0;
  timerHtml = '';
  isNeedRegistered?: boolean;
  phoneWatcher?: () => void;
  protected readonly steps: LoginSteps<CodeStatusType> = {
    phone: {
      title: () => this.$translate.instant('Js.Login.Code.PhoneTitle'),
      description: () => this.$translate.instant('Js.Login.Code.PhoneDescription'),
      showAuthMethods: true
    },
    confirmation: {
      title: () => this.$translate.instant('Js.Login.Code.ConfirmationTitle'),
      description: () => this.settings?.CodeDescription ?? undefined,
      back: 'phone'
    },
    registration: {
      title: () => this.$translate.instant('Js.Login.Code.RegistrationTitle'),
      description: () => this.$translate.instant('Js.Login.Code.RegistrationDescription'),
      back: 'phone'
    }
  };
  protected readonly backEventName = 'backLoginCode';
  protected readonly returnErrorKey = 'Js.Login.Code.ReturnError';
  private countdownInterval?: IPromise<unknown>;

  /* @ngInject */
  constructor(readonly loginService: ILoginService, toaster: IToasterService, $translate: translate.ITranslateService, $scope: IScope, readonly $interval: IIntervalService, readonly $window: IWindowService, readonly $sce: ISCEService, readonly captchaService: ICaptchaService, readonly phoneService: IPhoneService, readonly phoneCodeLength: number, readonly $q: IQService) {
    super($scope, toaster, $translate);
  }
  $onInit() {
    this.initPhone();
    super.$onInit();
  }
  protected loadStep() {
    return this.loginService.getLoginCodeSettings().then(settings => this.settings = settings);
  }
  $onDestroy() {
    if (this.phoneWatcher != null) {
      this.phoneWatcher();
    }
    this.cancelCountdown();
  }
  sendPhone() {
    if (typeof this.phone === 'undefined' || this.phone === null || this.phone === '' || this.phone.length === 0) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Code.ErrorEmptyPhone'));
      return;
    }
    if (!this.enableRetry) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Code.RetryError', {
        sec: this.countdownSeconds
      }));
      return;
    }
    const phone = this.phone;
    let captcha: Captcha | null = null;
    this.captchaService.confirmIfExist('sendCode').then(captchaResult => {
      captcha = captchaResult;
      return this.loginService.authorizationData({
        data: phone,
        inputValue: captcha?.inputValue,
        captchaId: captcha?.captchaId,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(authorizationStatus => {
      this.isNeedRegistered = authorizationStatus[0] !== 'y';
      this.setCountdown(30);
      this.toaster.pop('info', '', this.$translate.instant('Js.Login.Code.CodeSent') + this.phone);
      this.changeStatus('confirmation');
    }).catch(error => {
      if (!isCaptchaCancelled(error)) {
        this.toaster.pop('error', '', error.message);
      }
    }).finally(() => {
      if (captcha !== null) {
        captcha.close();
      }
    });
  }
  sendCode() {
    if (typeof this.phone === 'undefined' || this.phone === null || this.phone === '' || this.phone.length === 0) {
      return this.$q.resolve({
        success: false
      });
    }
    if (!this.enableRetry) {
      this.toaster.pop('error', '', this.$translate.instant('Js.Login.Code.RetryError', {
        sec: this.countdownSeconds
      }));
      return this.$q.resolve({
        success: false
      });
    }
    const phone = this.phone;
    let captcha: Captcha | null = null;
    return this.captchaService.confirmIfExist('sendCode').then(captchaResult => {
      captcha = captchaResult;
      return this.phoneService.sendCode({
        phone,
        signUp: this.isNeedRegistered === true,
        inputValue: captcha?.inputValue,
        captchaId: captcha?.captchaId,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(response => {
      this.setCountdown(response.secondsToRetry);
      this.toaster.pop('info', '', this.$translate.instant('Js.Login.Code.CodeSent') + this.phone);
      this.changeStatus('confirmation');
      return {
        success: true
      };
    }).catch(error => {
      if (!isCaptchaCancelled(error)) {
        this.toaster.pop('error', '', error.message);
      }
      return {
        success: false
      };
    }).finally(() => {
      if (captcha !== null) {
        captcha.close();
      }
    });
  }
  setCountdown(seconds: number) {
    this.cancelCountdown();
    if (!Number.isFinite(seconds) || seconds <= 0) {
      this.countdownSeconds = 0;
      this.enableRetry = true;
      this.timerHtml = '';
      return;
    }
    this.enableRetry = false;
    this.countdownSeconds = seconds;
    this.timerHtml = this.$translate.instant('Js.Login.Code.RetryPhoneCountdownText', {
      sec: this.countdownSeconds
    });
    this.countdownInterval = this.$interval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        this.enableRetry = true;
        this.timerHtml = '';
        this.cancelCountdown();
      } else {
        this.timerHtml = this.$translate.instant('Js.Login.Code.RetryPhoneCountdownText', {
          sec: this.countdownSeconds
        });
      }
    }, 1000, seconds);
  }
  confirmCode(code: string) {
    this.code = code;
    if (typeof this.code === 'undefined' || this.code === null || this.code.length < this.phoneCodeLength) {
      return;
    }
    if (typeof this.phone === 'undefined' || this.phone === null) {
      return;
    }
    const phone = this.phone;
    let captcha: Captcha | null = null;
    return this.captchaService.confirmIfExist('auth').then(captchaResult => {
      captcha = captchaResult;
      return this.phoneService.confirmCode({
        phone,
        code,
        signUp: this.isNeedRegistered === true,
        inputValue: captcha?.inputValue,
        captchaId: captcha?.captchaId,
        captchaInstanceId: captcha?.instanceId
      });
    }).then(() => {
      if (this.isNeedRegistered) {
        this.changeStatus('registration');
      } else if (typeof this.redirectTo !== 'undefined' && this.redirectTo !== null && this.redirectTo !== '') {
        this.$window.location.assign(this.redirectTo);
      } else {
        this.$window.location.reload();
      }
    }).catch(error => {
      if (!isCaptchaCancelled(error)) {
        this.toaster.pop('error', '', error.message);
      }
    }).finally(() => {
      if (captcha !== null) {
        captcha.close();
      }
    });
  }
  private initPhone = () => {
    if (this.parentCtrl != null) {
      this.phone = this.parentCtrl.phone;
    }
    this.phoneWatcher = this.$scope.$watch('$ctrl.phone', (newValue, _oldValue) => {
      if (this.parentCtrl != null && typeof newValue === 'string') {
        this.parentCtrl.phone = newValue;
      }
    });
  };
  private cancelCountdown() {
    if (this.countdownInterval != null) {
      this.$interval.cancel(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }
}
LoginCodeController.$inject = ["loginService", "toaster", "$translate", "$scope", "$interval", "$window", "$sce", "captchaService", "phoneService", "phoneCodeLength", "$q"];